import { Component, Input, forwardRef, model, signal, Optional, Self } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

export interface Option {
  label: string;
  value: any;
}

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [],
  template: `
    <div class="flex flex-col gap-1">
      <label *ngIf="label" [for]="id" class="text-sm font-medium text-slate-700">
        {{ label }}
        <span *ngIf="required" class="text-danger-500">*</span>
      </label>
      <select
        [id]="id"
        [ngModel]="value()"
        (ngModelChange)="onChange($event)"
        [disabled]="disabled"
        [required]="required"
        (blur)="onBlur()"
        class="w-full rounded border px-3 py-2 text-sm transition-colors disabled:bg-slate-50 disabled:text-slate-500"
        [class.border-danger-300]="shouldShowError()"
        [class.focus:border-danger-500]="shouldShowError()"
        [class.focus:ring-danger-500]="shouldShowError()"
        [class.border-slate-300]="!shouldShowError()"
        [class.focus:border-primary-500]="!shouldShowError()"
        [class.focus:ring-primary-500]="!shouldShowError()"
      >
        <option value="" [disabled]="required">{{ placeholder }}</option>
        <option *ngFor="let option of options" [value]="option.value">
          {{ option.label }}
        </option>
      </select>

      <div *ngIf="shouldShowError()" class="mt-1 text-sm text-danger-600">
        {{ getErrorMessage() }}
      </div>
    </div>
  `
})
export class SelectComponent implements ControlValueAccessor {
  @Input() id: string = '';
  @Input() label?: string;
  @Input() options: Option[] = [];
  @Input() placeholder: string = 'Selecteer een optie';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() error?: string;

  value = model<any>(null);
  touched = false;

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange = (value: any) => {
    this.value.set(value);
    this.onChangeCallback(value);
  };

  private onChangeCallback = (value: any) => {};

  onTouched = () => {};

  onBlur() {
    this.touched = true;
    this.onTouched();
  }

  shouldShowError(): boolean {
    if (this.error) return true;
    if (this.ngControl) {
      return !!(
        this.ngControl.touched &&
        this.ngControl.errors
      );
    }
    return this.required && this.touched && !this.value();
  }

  getErrorMessage(): string {
    if (this.error) return this.error;
    if (this.ngControl?.errors?.['required'] || (this.required && !this.value())) {
      return 'Dit veld is verplicht';
    }
    return '';
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
